// Single source of truth for "can the current user do X?" in the UI.
//
// Source data: ds.team.getCurrentMember() — joins tenant_members with the
// member's role and its permissions in two RPCs (one for members, one for
// role_permissions). Cached for 5 minutes; invalidated on accept-invite
// and on owner/admin changing the member's role.

import { useQuery } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useAuth } from '@/lib/auth-context';
import {
  hasPermission,
  PERMISSION_MODULES,
  type PermissionAction,
  type PermissionModule,
  type MemberWithRole,
} from '@erp/domain/permissions';
import type { TenantMember } from '@erp/data-access/interfaces';

const CURRENT_MEMBER_KEY = ['current-user-permissions'] as const;

interface UsePermissionsResult {
  member: TenantMember | null;
  /** Convenience accessor matching the hasPermission contract. */
  can: (module: PermissionModule | string, action: PermissionAction) => boolean;
  /** True for the tenant Owner (the original signup user). */
  isOwner: boolean;
  /** True for Owner OR system Admin role. */
  isAdmin: boolean;
  isLoading: boolean;
}

export function usePermissions(): UsePermissionsResult {
  const ds = useDataService();
  const { user } = useAuth();

  const q = useQuery({
    queryKey: CURRENT_MEMBER_KEY,
    queryFn: () => ds.team.getCurrentMember(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const member = q.data ?? null;
  const memberShape: MemberWithRole | null = member
    ? {
        roleSlug: member.roleSlug,
        permissions: member.permissions ?? [],
      }
    : null;

  const can = (
    module: PermissionModule | string,
    action: PermissionAction,
  ): boolean => hasPermission(memberShape, module, action);

  return {
    member,
    can,
    isOwner: !!member?.isOwner,
    isAdmin: !!member && (member.isOwner || member.roleSlug === 'admin'),
    isLoading: q.isLoading,
  };
}

/** Convenience for components that only need a single check. */
export function useCan(
  module: PermissionModule | string,
  action: PermissionAction,
): boolean {
  return usePermissions().can(module, action);
}

/** Re-export the canonical module list so consumers don't need to import
 *  from @erp/domain/permissions in addition to this hook. */
export { PERMISSION_MODULES };
export type { PermissionAction, PermissionModule };
